import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Card, Button, TextField, TextareaField } from '@dopomogai/ui';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Sparkles, Target, FileText, Palette, Check } from 'lucide-react';
import { PresentationTheme } from '@dopomogai/types';
import clsx from 'clsx';

export interface OnboardingData {
  title: string;
  goal: string;
  content: string;
  style: 'professional' | 'creative' | 'minimalist';
  theme: PresentationTheme;
}

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void; // For skipping or closing
  onGenerate: (data: OnboardingData) => void;
  isGenerating?: boolean;
}

// Define theme presets directly in the component for encapsulation
const visualStyles = [
    {
      id: 'professional' as const, nameKey: 'styles.professional.name', descriptionKey: 'styles.professional.description',
      theme: {
        colors: { primary: '#1e293b', secondary: '#3b82f6', tertiary: '#f8fafc', quaternary: '#e2e8f0' },
        fonts: { header: 'Inter, system-ui, sans-serif', text: 'Inter, system-ui, sans-serif', monospace: 'JetBrains Mono, monospace' },
        fontSizes: { h1: '3.5rem', h2: '2.5rem', h3: '2rem', h4: '1.5rem', h5: '1.25rem', text: '1rem' },
        space: [0, 4, 8, 16, 32, 64, 128],
      }
    },
    {
      id: 'creative' as const, nameKey: 'styles.creative.name', descriptionKey: 'styles.creative.description',
      theme: {
        colors: { primary: '#7c3aed', secondary: '#f59e0b', tertiary: '#fef3c7', quaternary: '#ede9fe' },
        fonts: { header: 'Inter, system-ui, sans-serif', text: 'Inter, system-ui, sans-serif', monospace: 'JetBrains Mono, monospace' },
        fontSizes: { h1: '4rem', h2: '2.75rem', h3: '2.25rem', h4: '1.75rem', h5: '1.5rem', text: '1.125rem' },
        space: [0, 6, 12, 24, 48, 96, 192],
      }
    },
    {
      id: 'minimalist' as const, nameKey: 'styles.minimalist.name', descriptionKey: 'styles.minimalist.description',
      theme: {
        colors: { primary: '#374151', secondary: '#6b7280', tertiary: '#ffffff', quaternary: '#f9fafb' },
        fonts: { header: 'Inter, system-ui, sans-serif', text: 'Inter, system-ui, sans-serif', monospace: 'JetBrains Mono, monospace' },
        fontSizes: { h1: '3rem', h2: '2.25rem', h3: '1.875rem', h4: '1.5rem', h5: '1.25rem', text: '1rem' },
        space: [0, 8, 16, 32, 64, 128, 256],
      }
    }
];

export const OnboardingModal: React.FC<OnboardingModalProps> = ({ isOpen, onClose, onGenerate, isGenerating = false }) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<OnboardingData>>({ style: 'professional' });
  const totalSteps = 4;

  const handleNext = () => currentStep < totalSteps && setCurrentStep(currentStep + 1);
  const handlePrevious = () => currentStep > 1 && setCurrentStep(currentStep - 1);

  const handleGenerateClick = () => {
    const selectedStyle = visualStyles.find(style => style.id === formData.style);
    if (formData.title && formData.goal && formData.content && selectedStyle) {
      onGenerate({
        title: formData.title,
        goal: formData.goal,
        content: formData.content,
        style: formData.style!,
        theme: selectedStyle.theme
      });
    }
  };
  
  const isStepValid = () => {
    switch (currentStep) {
      case 1: return !!formData.title && !!formData.goal;
      case 2: return !!formData.content && formData.content.length > 20;
      case 3: return !!formData.style;
      case 4: return true;
      default: return false;
    }
  };

  const stepVariants = { enter: { opacity: 0, x: 30 }, center: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -30 } };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" hideCloseButton>
        <div className="flex flex-col h-[70vh]">
             {/* Header with Progress Bar */}
             <div className="p-6 border-b-2 border-black">
                <div className="flex items-center justify-between mb-4">
                    <h1 className="text-2xl font-bold text-slate-900">{t('create_presentation_title', 'Create Presentation with AI')}</h1>
                    <span className="text-sm font-semibold text-slate-500">{t('step_of', 'Step {{current}} of {{total}}', { current: currentStep, total: totalSteps })}</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2 border border-black">
                    <motion.div 
                        className="bg-emerald-500 h-full rounded-full border-r border-black"
                        initial={{ width: 0 }}
                        animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
                        transition={{ type: 'spring', stiffness: 100, damping: 20 }}
                    />
                </div>
             </div>

            {/* Step Content Area */}
            <div className="p-8 flex-grow overflow-y-auto">
                 <AnimatePresence mode="wait">
                    {currentStep === 1 && (
                        <motion.div key="step1" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} className="space-y-6">
                            <div className="text-center">
                                <h2 className="text-xl font-bold text-slate-800">{t('step1.title', 'Define Your Presentation')}</h2>
                                <p className="text-slate-600">{t('step1.description', 'Tell us about your presentation and what you want to achieve.')}</p>
                            </div>
                            <TextField label={t('presentation_title_label', 'Presentation Title')} value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder={t('title_placeholder', 'e.g., Q3 Marketing Strategy')} />
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">{t('primary_goal_label', 'What is the primary goal?')}</label>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                  {['pitch', 'teach', 'update', 'proposal', 'training', 'other'].map(goal => (
                                      <Button key={goal} text={t(`goals.${goal}`)} variant={formData.goal === goal ? 'primary' : 'outline'} onClick={() => setFormData({...formData, goal})}/>
                                  ))}
                                </div>
                            </div>
                        </motion.div>
                    )}
                    {currentStep === 2 && (
                         <motion.div key="step2" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} className="space-y-6">
                            <div className="text-center">
                                <h2 className="text-xl font-bold text-slate-800">{t('step2.title', 'Provide Your Content')}</h2>
                                <p className="text-slate-600">{t('step2.description', 'Paste your outline, raw text, or key points. The AI will structure it.')}</p>
                            </div>
                            <TextareaField
                                label={t('content_label', 'Content or Outline')}
                                value={formData.content || ''}
                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                placeholder={t('content_placeholder', 'Start with a high-level overview...')}
                                rows={10}
                                showCharacterCount
                                maxLength={5000}
                            />
                        </motion.div>
                    )}
                    {currentStep === 3 && (
                        <motion.div key="step3" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} className="space-y-6">
                            <div className="text-center">
                                <h2 className="text-xl font-bold text-slate-800">{t('step3.title', 'Choose a Visual Style')}</h2>
                                <p className="text-slate-600">{t('step3.description', 'Select a theme to define the look and feel of your slides.')}</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {visualStyles.map(style => (
                                    <div
                                        key={style.id}
                                        onClick={() => setFormData({ ...formData, style: style.id })}
                                        className={clsx(
                                            'group cursor-pointer bg-white rounded-xl border-2 p-4 text-center',
                                            'transform transition-all duration-200',
                                            formData.style === style.id ? 'border-emerald-500 shadow-[3px_3px_0_0_#059669]' : 'border-black',
                                            'hover:-translate-y-1 hover:-translate-x-1',
                                            formData.style !== style.id && 'hover:shadow-[4px_4px_0_0_rgb(17,24,39)]',
                                            'active:translate-x-0 active:translate-y-0 active:shadow-none'
                                        )}
                                    >
                                        <div className="flex justify-center space-x-2 mb-3">
                                            {Object.values(style.theme.colors).slice(0, 4).map((color, i) => (
                                                <div key={i} className="w-6 h-6 rounded-full border-2 border-black" style={{ backgroundColor: color }} />
                                            ))}
                                        </div>
                                        <h3 className="font-bold text-slate-800">{t(style.nameKey)}</h3>
                                        <p className="text-xs text-slate-600">{t(style.descriptionKey)}</p>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                    {currentStep === 4 && (
                         <motion.div key="step4" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} className="space-y-6 text-center">
                            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-black">
                                <Sparkles size={32} className="text-emerald-700" />
                            </div>
                            <h2 className="text-2xl font-bold text-slate-800">{t('step4.title', 'Ready to Generate?')}</h2>
                            <p className="text-slate-600 max-w-md mx-auto">{t('step4.description', 'The AI will now create a presentation based on your input. This may take a moment.')}</p>
                            <Card noHoverEffect className="text-left w-full max-w-lg mx-auto">
                                <ul>
                                    <li className="flex items-center justify-between py-2 border-b border-slate-200"><span className="font-semibold text-slate-600">{t('summary.title')}</span> <span className="text-slate-900 truncate max-w-[200px]">{formData.title}</span></li>
                                    <li className="flex items-center justify-between py-2 border-b border-slate-200"><span className="font-semibold text-slate-600">{t('summary.goal')}</span> <span className="text-slate-900 capitalize">{formData.goal}</span></li>
                                    <li className="flex items-center justify-between pt-2"><span className="font-semibold text-slate-600">{t('summary.style')}</span> <span className="text-slate-900 capitalize">{formData.style}</span></li>
                                </ul>
                            </Card>
                         </motion.div>
                    )}
                 </AnimatePresence>
            </div>

            {/* Footer Navigation */}
            <div className="p-4 border-t-2 border-black bg-slate-50 flex justify-between items-center">
                <Button variant="outline" onClick={handlePrevious} disabled={currentStep === 1 || isGenerating} icon={<ChevronLeft size={16}/>} text={t('common:previous')} />
                
                {currentStep < totalSteps ? (
                    <Button variant="primary" onClick={handleNext} disabled={!isStepValid() || isGenerating} icon={<ChevronRight size={16}/>} iconPosition="right" text={t('common:next')} />
                ) : (
                    <Button variant="primary" onClick={handleGenerateClick} disabled={!isStepValid() || isGenerating} icon={<Sparkles size={16}/>} text={isGenerating ? t('common:generating', 'Generating...') : t('generate_presentation_button', 'Generate Presentation')} />
                )}
            </div>
        </div>
    </Modal>
  );
};